import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface InventoryItem {
  id: string;
  name: string;
  category: 'bahan-utama' | 'bumbu' | 'sayuran' | 'daging' | 'seafood' | 'minuman';
  currentStock: number;
  minStock: number;
  maxStock: number;
  unit: string;
  price: number;
  supplier: string;
  lastUpdated: string;
  status: 'normal' | 'low' | 'critical' | 'overstock';
  expiryDate?: string;
}

export default function InventoryManagement() {
  // Modified state definitions
  const [inventory, setInventory] = useState([
    {
      id: 'beef-premium',
      name: 'Daging Sapi Premium',
      category: 'daging' as const,
      currentStock: 5,
      minStock: 10,
      maxStock: 50,
      unit: 'kg',
      price: 180000,
      supplier: 'PT. Sumber Protein',
      lastUpdated: '2024-01-20T10:00:00Z',
      status: 'low' as const,
      expiryDate: '2024-02-15'
    },
    {
      id: 'rice-sushi',
      name: 'Beras Sushi Premium',
      category: 'bahan-utama' as const,
      currentStock: 8,
      minStock: 15,
      maxStock: 60,
      unit: 'kg',
      price: 75000,
      supplier: 'Import Jepang Co.',
      lastUpdated: '2024-01-18T10:00:00Z',
      status: 'low' as const,
      expiryDate: '2024-03-01'
    },
    {
      id: 'nori-seaweed',
      name: 'Rumput Laut Nori',
      category: 'bahan-utama' as const,
      currentStock: 25,
      minStock: 50,
      maxStock: 200,
      unit: 'sheet',
      price: 2500,
      supplier: 'Ocean Fresh Ltd.',
      lastUpdated: '2024-01-15T10:00:00Z',
      status: 'low' as const,
      expiryDate: '2024-04-10'
    },
    {
      id: 'soy-sauce',
      name: 'Kecap Asin Premium',
      category: 'bumbu' as const,
      currentStock: 3,
      minStock: 8,
      maxStock: 25,
      unit: 'botol',
      price: 45000,
      supplier: 'Kimchi Trading',
      lastUpdated: '2024-01-22T10:00:00Z',
      status: 'critical' as const,
      expiryDate: '2024-06-15'
    },
    {
      id: 'tempura-flour',
      name: 'Tepung Tempura',
      category: 'bahan-utama' as const,
      currentStock: 12,
      minStock: 20,
      maxStock: 80,
      unit: 'kg',
      price: 35000,
      supplier: 'Flour Master Co.',
      lastUpdated: '2024-01-19T10:00:00Z',
      status: 'low' as const,
      expiryDate: '2024-05-20'
    },
    {
      id: 'salmon-fresh',
      name: 'Salmon Segar Premium',
      category: 'seafood' as const,
      currentStock: 2,
      minStock: 5,
      maxStock: 15,
      unit: 'kg',
      price: 220000,
      supplier: 'Ocean Fresh Ltd.',
      lastUpdated: '2024-01-23T10:00:00Z',
      status: 'critical' as const,
      expiryDate: '2024-01-25'
    },
    {
      id: 'miso-paste',
      name: 'Pasta Miso Premium',
      category: 'bumbu' as const,
      currentStock: 15,
      minStock: 10,
      maxStock: 40,
      unit: 'kg',
      price: 85000,
      supplier: 'Kimchi Trading',
      lastUpdated: '2024-01-21T10:00:00Z',
      status: 'normal' as const,
      expiryDate: '2024-07-10'
    },
    {
      id: 'green-tea',
      name: 'Teh Hijau Matcha',
      category: 'minuman' as const,
      currentStock: 30,
      minStock: 25,
      maxStock: 100,
      unit: 'pack',
      price: 125000,
      supplier: 'Import Jepang Co.',
      lastUpdated: '2024-01-17T10:00:00Z',
      status: 'normal' as const,
      expiryDate: '2024-12-31'
    }
  ]);

  const [showPurchaseOrderForm, setShowPurchaseOrderForm] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [purchaseOrder, setPurchaseOrder] = useState({
    supplier: '',
    expectedDelivery: '',
    notes: '',
    items: [] as any[]
  });
  const [isSubmittingPO, setIsSubmittingPO] = useState(false);

  // Original state definitions
  const [filter, setFilter] = useState<'all' | 'low' | 'critical' | 'normal'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isAddingStock, setIsAddingStock] = useState(false);
  const [stockAmount, setStockAmount] = useState('');

  // New functions for Purchase Order
  const getLowStockItems = () => {
    return inventory.filter(item => item.currentStock <= item.minStock);
  };

  const handleCreatePurchaseOrder = () => {
    const lowStockItems = getLowStockItems();
    const suggestedItems = lowStockItems.map(item => ({
      ...item,
      suggestedQuantity: Math.max(item.minStock * 2 - item.currentStock, item.minStock),
      selected: true,
      pricePerUnit: item.price // Ensure consistent naming
    }));

    setPurchaseOrder({
      supplier: '',
      expectedDelivery: '',
      notes: '',
      items: suggestedItems
    });
    setShowPurchaseOrderForm(true);
  };

  const handleItemSelection = (itemId: string, selected: boolean) => {
    setPurchaseOrder(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, selected } : item
      )
    }));
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    setPurchaseOrder(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, suggestedQuantity: Math.max(1, quantity) } : item
      )
    }));
  };

  const getTotalPOValue = () => {
    return purchaseOrder.items
      .filter(item => item.selected)
      .reduce((total, item) => total + ((item.pricePerUnit || item.price) * item.suggestedQuantity), 0);
  };

  const handleSubmitPurchaseOrder = async () => {
    if (purchaseOrder.items.filter(item => item.selected).length === 0) {
      alert('Pilih minimal satu item untuk purchase order!');
      return;
    }

    if (!purchaseOrder.supplier) {
      alert('Silakan pilih supplier terlebih dahulu!');
      return;
    }

    setIsSubmittingPO(true);

    try {
      // Generate PO number
      const poNumber = 'PO-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 6).toUpperCase();
      
      // Simulate API call to create purchase order
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate processing time

      // Mock success response
      const selectedItems = purchaseOrder.items.filter(item => item.selected);
      const totalValue = getTotalPOValue();

      // Show success message with PO details
      const itemsList = selectedItems.map(item => 
        `• ${item.name}: ${item.suggestedQuantity} ${item.unit} (Rp ${((item.pricePerUnit || item.price) * item.suggestedQuantity).toLocaleString('id-ID')})`
      ).join('\n');

      alert(`✅ Purchase Order berhasil dibuat!\n\nNomor PO: ${poNumber}\nSupplier: ${purchaseOrder.supplier}\nTotal Items: ${selectedItems.length}\nNilai Total: Rp ${totalValue.toLocaleString('id-ID')}\n\nDetail Items:\n${itemsList}\n\nPO telah dikirim ke supplier dan akan diproses sesuai jadwal pengiriman.`);

      // Reset form and close modal
      setShowPurchaseOrderForm(false);
      setPurchaseOrder({
        supplier: '',
        expectedDelivery: '',
        notes: '',
        items: []
      });

      // Optional: You could also update inventory status or add PO tracking here
      console.log('Purchase Order created:', {
        poNumber,
        supplier: purchaseOrder.supplier,
        items: selectedItems,
        totalValue,
        createdAt: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error creating purchase order:', error);
      alert('❌ Gagal membuat purchase order. Silakan coba lagi.');
    } finally {
      setIsSubmittingPO(false);
    }
  };

  const suppliers = [
    'PT. Sumber Protein',
    'Import Jepang Co.',
    'Ocean Fresh Ltd.',
    'Kimchi Trading',
    'Flour Master Co.',
    'Fresh Vegetable Corp.',
    'Premium Spice Ltd.'
  ];

  // Existing original functions and computed values
  const filteredInventory = inventory.filter(item => {
    const matchesFilter = filter === 'all' || item.status === filter;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status: InventoryItem['status']) => {
    switch (status) {
      case 'critical': return 'text-red-400 bg-red-400/20';
      case 'low': return 'text-orange-400 bg-orange-400/20';
      case 'overstock': return 'text-purple-400 bg-purple-400/20';
      case 'normal': return 'text-green-400 bg-green-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  const getCategoryIcon = (category: InventoryItem['category']) => {
    switch (category) {
      case 'bahan-utama': return 'ri-bowl-line';
      case 'bumbu': return 'ri-cup-line';
      case 'sayuran': return 'ri-leaf-line';
      case 'daging': return 'ri-restaurant-line';
      case 'seafood': return 'ri-fish-line';
      case 'minuman': return 'ri-cup-line';
      default: return 'ri-box-line';
    }
  };

  const getCategoryName = (category: InventoryItem['category']) => {
    switch (category) {
      case 'bahan-utama': return 'Bahan Utama';
      case 'bumbu': return 'Bumbu & Saus';
      case 'sayuran': return 'Sayuran';
      case 'daging': return 'Daging';
      case 'seafood': return 'Seafood';
      case 'minuman': return 'Minuman';
      default: return 'Lainnya';
    }
  };

  const updateStock = (itemId: string, newStock: number) => {
    setInventory(prev => prev.map(item => {
      if (item.id === itemId) {
        let status: InventoryItem['status'] = 'normal';
        if (newStock <= 0) status = 'critical';
        else if (newStock <= item.minStock) status = 'low';
        else if (newStock > item.maxStock) status = 'overstock';
        
        return {
          ...item,
          currentStock: newStock,
          status,
          lastUpdated: new Date().toISOString()
        };
      }
      return item;
    }));
  };

  const handleAddStock = () => {
    if (selectedItem && stockAmount) {
      const newStock = selectedItem.currentStock + parseInt(stockAmount);
      updateStock(selectedItem.id, newStock);
      setSelectedItem(null);
      setIsAddingStock(false);
      setStockAmount('');
    }
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 3;
  };

  return (
    <div className="space-y-6">
      {/* Header with Purchase Order Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white mb-2">Manajemen Inventori</h3>
          <p className="text-gray-300">Kelola stok bahan baku dan buat purchase order</p>
        </div>
        <button
          onClick={handleCreatePurchaseOrder}
          className="inline-flex items-center justify-center font-semibold rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500 px-4 py-2 text-sm w-auto bg-orange-600 hover:bg-orange-700"
        >
          <i className="ri-shopping-cart-line mr-2 w-4 h-4 flex items-center justify-center"></i>
          Buat Purchase Order
        </button>
      </div>

      {/* Low Stock Alert */}
      {getLowStockItems().length > 0 && (
        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 backdrop-blur-sm rounded-2xl border border-red-500/30 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <i className="ri-alert-line text-red-400 w-6 h-6 flex items-center justify-center"></i>
            <h4 className="text-lg font-bold text-red-400">Peringatan Stok Rendah</h4>
          </div>
          <p className="text-gray-300 mb-4">
            {getLowStockItems().length} item memiliki stok di bawah batas minimum dan perlu segera direstok.
          </p>
          <div className="flex flex-wrap gap-2">
            {getLowStockItems().map(item => (
              <span key={item.id} className="bg-red-500/20 text-red-300 px-3 py-1 rounded-full text-sm border border-red-500/30">
                {item.name}: {item.currentStock} {item.unit}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Header & Controls (original) */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Manajemen Inventory</h2>
          <p className="text-gray-400">Kelola stok bahan makanan dan minuman</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 flex items-center justify-center"></i>
            <input
              type="text"
              placeholder="Cari bahan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none w-full sm:w-64 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'Semua Item', count: inventory.length },
          { id: 'critical', label: 'Kritis', count: inventory.filter(i => i.status === 'critical').length },
          { id: 'low', label: 'Stok Rendah', count: inventory.filter(i => i.status === 'low').length },
          { id: 'normal', label: 'Normal', count: inventory.filter(i => i.status === 'normal').length }
        ].map((filterItem) => (
          <button
            key={filterItem.id}
            onClick={() => setFilter(filterItem.id as any)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              filter === filterItem.id
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {filterItem.label} ({filterItem.count})
          </button>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-red-900/20 to-red-800/20 backdrop-blur-sm rounded-2xl border border-red-500/20 p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
              <i className="ri-alert-line text-white w-6 h-6 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-400">{inventory.filter(i => i.status === 'critical').length}</div>
              <div className="text-gray-300 text-sm">Stok Kritis</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-900/20 to-orange-800/20 backdrop-blur-sm rounded-2xl border border-orange-500/20 p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center">
              <i className="ri-error-warning-line text-white w-6 h-6 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-400">{inventory.filter(i => i.status === 'low').length}</div>
              <div className="text-gray-300 text-sm">Stok Rendah</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-900/20 to-green-800/20 backdrop-blur-sm rounded-2xl border border-green-500/20 p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
              <i className="ri-check-line text-white w-6 h-6 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{inventory.filter(i => i.status === 'normal').length}</div>
              <div className="text-gray-300 text-sm">Stok Normal</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 backdrop-blur-sm rounded-2xl border border-blue-500/20 p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <i className="ri-archive-line text-white w-6 h-6 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{inventory.length}</div>
              <div className="text-gray-300 text-sm">Total Item</div>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Grid */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredInventory.map((item) => (
          <div
            key={item.id}
            className={`bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border transition-all duration-300 overflow-hidden ${
              item.status === 'critical' ? 'border-red-500/50 shadow-lg shadow-red-500/20' :
              item.status === 'low' ? 'border-orange-500/50 shadow-lg shadow-orange-500/20' :
              'border-gray-600/50 hover:border-red-500/50'
            }`}
          >
            <div className="p-6">
              {/* Item Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${getCategoryIcon(item.category)} text-white w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-white truncate">{item.name}</h3>
                    <p className="text-gray-400 text-sm">{getCategoryName(item.category)}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(item.status)}`}>
                    {item.status === 'critical' ? 'KRITIS' :
                     item.status === 'low' ? 'RENDAH' :
                     item.status === 'overstock' ? 'BERLEBIH' : 'NORMAL'}
                  </span>
                  {item.expiryDate && isExpiringSoon(item.expiryDate) && (
                    <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-400/20 text-red-400">
                      ⚠️ EXPIRED SOON
                    </span>
                  )}
                </div>
              </div>

              {/* Stock Information */}
              <div className="space-y-4 mb-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Stok Saat Ini</span>
                    <span className="text-white font-bold">{item.currentStock} {item.unit}</span>
                  </div>
                  <div className="bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        item.status === 'critical' ? 'bg-red-500' :
                        item.status === 'low' ? 'bg-orange-500' :
                        item.status === 'overstock' ? 'bg-purple-500' :
                        'bg-green-500'
                      }`}
                      style={{
                        width: `${Math.min((item.currentStock / item.maxStock) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Min: {item.minStock}</span>
                    <span>Max: {item.maxStock}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Harga/Unit:</span>
                    <div className="text-white font-semibold">Rp {item.price?.toLocaleString('id-ID') || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Supplier:</span>
                    <div className="text-white font-semibold truncate">{item.supplier}</div>
                  </div>
                </div>

                {item.expiryDate && (
                  <div className="text-sm">
                    <span className="text-gray-400">Expired:</span>
                    <div className={`font-semibold ${isExpiringSoon(item.expiryDate) ? 'text-red-400' : 'text-white'}`}>
                      {new Date(item.expiryDate).toLocaleDateString('id-ID')}
                    </div>
                  </div>
                )}

                <div className="text-sm">
                  <span className="text-gray-400">Update Terakhir:</span>
                  <div className="text-white font-semibold">
                    {new Date(item.lastUpdated).toLocaleString('id-ID')}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsAddingStock(true);
                    }}
                    className="whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Tambah Stok
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedItem(item)}
                    className="whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Edit
                  </Button>
                </div>
                
                {(item.status === 'critical' || item.status === 'low') && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full bg-orange-600 hover:bg-orange-700 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-shopping-cart-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Buat Purchase Order
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredInventory.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-archive-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
          </div>
          <p className="text-gray-400 text-lg">Tidak ada item inventory yang ditemukan</p>
        </div>
      )}

      {/* Add Stock Modal */}
      {isAddingStock && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-red-500/20 max-w-md w-full">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Tambah Stok</h3>
                <button
                  onClick={() => {
                    setIsAddingStock(false);
                    setSelectedItem(null);
                    setStockAmount('');
                  }}
                  className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>

              <div className="space-y-6">
                <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                  <h4 className="text-lg font-semibold text-white mb-2">{selectedItem.name}</h4>
                  <p className="text-gray-400">Stok saat ini: <span className="text-white font-bold">{selectedItem.currentStock} {selectedItem.unit}</span></p>
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Jumlah Tambahan ({selectedItem.unit})</label>
                  <input
                    type="number"
                    value={stockAmount}
                    onChange={(e) => setStockAmount(e.target.value)}
                    min="1"
                    placeholder="Masukkan jumlah"
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-sm"
                  />
                </div>

                {stockAmount && (
                  <div className="p-4 bg-green-500/20 rounded-xl border border-green-500/50">
                    <p className="text-green-400 font-semibold">
                      Stok setelah penambahan: {selectedItem.currentStock + parseInt(stockAmount || '0')} {selectedItem.unit}
                    </p>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleAddStock}
                    disabled={!stockAmount || parseInt(stockAmount) <= 0}
                    className="flex-1 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-check-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Konfirmasi
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      setIsAddingStock(false);
                      setSelectedItem(null);
                      setStockAmount('');
                    }}
                    className="whitespace-nowrap cursor-pointer"
                  >
                    Batal
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && !isAddingStock && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-red-500/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Detail Item: {selectedItem.name}</h3>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Nama Item</label>
                      <div className="text-white font-semibold">{selectedItem.name}</div>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Kategori</label>
                      <div className="text-white font-semibold">{getCategoryName(selectedItem.category)}</div>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Status</label>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedItem.status)}`}>
                        {selectedItem.status === 'critical' ? 'KRITIS' :
                         selectedItem.status === 'low' ? 'STOK RENDAH' :
                         selectedItem.status === 'overstock' ? 'BERLEBIH' : 'NORMAL'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Stok Saat Ini</label>
                      <div className="text-2xl font-bold text-white">{selectedItem.currentStock} {selectedItem.unit}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Min Stock</label>
                        <div className="text-white font-semibold">{selectedItem.minStock} {selectedItem.unit}</div>
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Max Stock</label>
                        <div className="text-white font-semibold">{selectedItem.maxStock} {selectedItem.unit}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Harga per Unit</label>
                    <div className="text-white font-semibold">Rp {selectedItem.price?.toLocaleString('id-ID') || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Supplier</label>
                    <div className="text-white font-semibold">{selectedItem.supplier}</div>
                  </div>
                </div>

                {selectedItem.expiryDate && (
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Tanggal Expired</label>
                    <div className={`font-semibold ${isExpiringSoon(selectedItem.expiryDate) ? 'text-red-400' : 'text-white'}`}>
                      {new Date(selectedItem.expiryDate).toLocaleDateString('id-ID')}
                      {isExpiringSoon(selectedItem.expiryDate) && (
                        <span className="ml-2 text-red-400">⚠️ Akan segera expired!</span>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Update Terakhir</label>
                  <div className="text-white font-semibold">{new Date(selectedItem.lastUpdated).toLocaleString('id-ID')}</div>
                </div>

                <div className="flex gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => {
                      setIsAddingStock(true);
                    }}
                    className="flex-1 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Tambah Stok
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setSelectedItem(null)}
                    className="whitespace-nowrap cursor-pointer"
                  >
                    Tutup
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Order Form Modal */}
      {showPurchaseOrderForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-red-500/20 p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Buat Purchase Order</h3>
                <p className="text-gray-300">Buat pesanan pembelian untuk stok yang hampir habis</p>
              </div>
              <button
                onClick={() => setShowPurchaseOrderForm(false)}
                className="w-10 h-10 bg-gray-700 hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            {/* PO Form */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Form Fields */}
              <div className="lg:col-span-1 space-y-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Supplier *</label>
                  <select
                    value={purchaseOrder.supplier}
                    onChange={(e) => setPurchaseOrder(prev => ({ ...prev, supplier: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:border-red-500 focus:outline-none transition-colors text-sm pr-8"
                    required
                  >
                    <option value="">Pilih Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier} value={supplier}>{supplier}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Tanggal Pengiriman</label>
                  <input
                    type="date"
                    value={purchaseOrder.expectedDelivery}
                    onChange={(e) => setPurchaseOrder(prev => ({ ...prev, expectedDelivery: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:border-red-500 focus:outline-none transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Catatan</label>
                  <textarea
                    value={purchaseOrder.notes}
                    onChange={(e) => setPurchaseOrder(prev => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:border-red-500 focus:outline-none transition-colors text-sm resize-none"
                    placeholder="Catatan khusus untuk supplier..."
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-600">
                  <h4 className="font-bold text-white mb-3">Ringkasan PO</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total Items:</span>
                      <span className="text-white font-bold">
                        {purchaseOrder.items.filter(item => item.selected).length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Nilai Total:</span>
                      <span className="text-yellow-400 font-bold">
                        Rp {getTotalPOValue().toLocaleString('id-ID')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Selection */}
              <div className="lg:col-span-2">
                <h4 className="text-lg font-bold text-white mb-4">Pilih Items untuk PO</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {purchaseOrder.items.map(item => (
                    <div key={item.id} className="bg-gray-900/50 rounded-xl p-4 border border-gray-600">
                      <div className="flex items-start space-x-4">
                        <div className="flex items-center pt-1">
                          <input
                            type="checkbox"
                            checked={item.selected}
                            onChange={(e) => handleItemSelection(item.id, e.target.checked)}
                            className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500 cursor-pointer"
                          />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-semibold text-white">{item.name}</h5>
                            <span className="text-red-400 text-sm">Stok: {item.currentStock} {item.unit}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <label className="block text-gray-300 text-xs mb-1">Kuantitas</label>
                              <input
                                type="number"
                                value={item.suggestedQuantity}
                                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                                min="1"
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-red-500 focus:outline-none"
                                disabled={!item.selected}
                              />
                            </div>
                            <div>
                              <label className="block text-gray-300 text-xs mb-1">Harga per {item.unit}</label>
                              <div className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-sm">
                                Rp {(item.pricePerUnit || item.price || 0).toLocaleString('id-ID')}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">Supplier: {item.supplier}</span>
                            <span className="text-yellow-400 font-bold">
                              Total: Rp {((item.pricePerUnit || item.price || 0) * item.suggestedQuantity).toLocaleString('id-ID')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-600">
              <button
                onClick={() => setShowPurchaseOrderForm(false)}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                onClick={handleSubmitPurchaseOrder}
                disabled={isSubmittingPO || purchaseOrder.items.filter(item => item.selected).length === 0 || !purchaseOrder.supplier}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmittingPO ? (
                  <>
                    <i className="ri-loader-4-line mr-2 w-4 h-4 flex items-center justify-center animate-spin"></i>
                    Membuat PO...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Kirim Purchase Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
